import { BRACKET_TAGS, DeckInsights, type OwnedCopy } from '@mtg/shared';
import { useState } from 'react';
import { Link } from 'wouter';
import { api } from '../api.ts';
import { formatDateTime, plural } from '../format.ts';
import { useApi } from '../useApi.ts';

const comboUrl = (id: string) => `https://commanderspellbook.com/combo/${encodeURIComponent(id)}/`;
const SHOW = 12;

/**
 * Combos and a bracket estimate from Commander Spellbook, and the combos one
 * card short, with where you already own that card.
 */
export function DeckInsightsPanel({ deckId }: { deckId: string }) {
  const [refresh, setRefresh] = useState(0);
  const insights = useApi(() => api(`/api/decks/${deckId}/insights${refresh ? '?refresh=1' : ''}`, DeckInsights), [deckId, refresh]);
  const [onlyOwned, setOnlyOwned] = useState<boolean | null>(null);
  const [showAll, setShowAll] = useState(false);

  if (insights.error) {
    return (
      <section className="panel insights">
        <p className="error">{insights.error}</p>
      </section>
    );
  }
  if (!insights.data) {
    return (
      <section className="panel insights">
        <p className="muted">Checking combos and bracket with Commander Spellbook…</p>
      </section>
    );
  }
  const d = insights.data;
  const tag = BRACKET_TAGS[d.bracket.tag];
  const ownedCount = d.nearMisses.filter((n) => n.owned.length > 0).length;
  const owned = onlyOwned ?? ownedCount > 0;
  const near = owned ? d.nearMisses.filter((n) => n.owned.length > 0) : d.nearMisses;
  const shown = showAll ? near : near.slice(0, SHOW);

  return (
    <details className="panel insights">
      <summary>
        <strong>{tag.bracket ? `Bracket ${tag.bracket}` : 'Not legal'}</strong>
        <span className="muted"> · {tag.label}</span>
        <span className="muted small">
          {' '}
          · {plural(d.combos.length, 'combo')} · {d.nearMisses.length} one card away
          {ownedCount > 0 && ` (${ownedCount} with a card you own)`}
        </span>
      </summary>

      <div className="stack insights-body">
        {d.outdated && <p className="warn small">Commander Spellbook couldn't be reached, so this is from before the deck's last change.</p>}

        <section>
          <h3>Bracket estimate</h3>
          {d.bracket.reasons.length ? (
            <ul className="insights-list">
              {d.bracket.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          ) : (
            <p className="muted small">No Game Changers, extra turns, mass land denial or fast two-card combos.</p>
          )}
        </section>

        <section>
          <h3>
            Combos in this deck <span className="muted">({d.combos.length})</span>
          </h3>
          {d.combos.length === 0 ? (
            <p className="muted small">None found.</p>
          ) : (
            <ul className="insights-list">
              {d.combos.map((c) => (
                <li key={c.id}>
                  <span>
                    <a href={comboUrl(c.id)} target="_blank" rel="noreferrer">
                      {c.cards.join(' + ')}
                    </a>
                    {c.fastTwoCard && (
                      <span className="badge" title="Spellbook counts it as a fast two-card combo, which sets bracket 4">
                        Fast
                      </span>
                    )}
                  </span>
                  <Produces items={c.produces} requires={c.requires} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3>
            One card away <span className="muted">({near.length})</span>
          </h3>
          {d.nearMisses.length > 0 && (
            <label className="row tight small">
              <input type="checkbox" checked={owned} onChange={(e) => setOnlyOwned(e.target.checked)} disabled={ownedCount === 0} />
              Only ones where you own the missing card ({ownedCount})
            </label>
          )}
          {near.length === 0 ? (
            <p className="muted small">{d.nearMisses.length ? 'You own none of the missing cards.' : 'None found.'}</p>
          ) : (
            <ul className="insights-list">
              {shown.map((n) => (
                <li key={n.id}>
                  <OneCardAway name={n.missing.name} cards={n.cards} />
                  <Where owned={n.owned} />
                  <Produces items={n.produces} requires={n.requires} href={comboUrl(n.id)} />
                </li>
              ))}
            </ul>
          )}
          {near.length > SHOW && (
            <button type="button" className="btn small" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show fewer' : `Show all ${near.length}`}
            </button>
          )}
        </section>

        {d.unmatched.length > 0 && <p className="muted small">Spellbook didn't recognise: {d.unmatched.join(', ')}.</p>}
        <p className="muted small row">
          <span>
            From <a href="https://commanderspellbook.com">Commander Spellbook</a> · checked {formatDateTime(d.fetchedAt)}
          </span>
          <button type="button" className="btn small" onClick={() => setRefresh((n) => n + 1)}>
            Check again
          </button>
        </p>
      </div>
    </details>
  );
}

/** "Add X to A + B", or for a combo that's the missing card alone, "Add X: a combo on its own". */
function OneCardAway({ name, cards }: { name: string; cards: string[] }) {
  const others = cards.filter((c) => c !== name);
  return (
    <span>
      Add <strong>{name}</strong>
      <span className="muted">{others.length ? ` to ${others.join(' + ')}` : ': a combo on its own'}</span>
    </span>
  );
}

function Produces({ items, requires, href }: { items: string[]; requires: string[]; href?: string }) {
  const list = items.slice(0, 3).join(', ') + (items.length > 3 ? ` +${items.length - 3} more` : '');
  return (
    <span className="muted small insights-produces">
      → {list}
      {requires.length > 0 && <> · needs {requires.join(', ')}</>}
      {href && (
        <>
          {' '}
          ·{' '}
          <a href={href} target="_blank" rel="noreferrer">
            combo
          </a>
        </>
      )}
    </span>
  );
}

/** Where your copies of the missing card are: drawer and divider, or the deck it's in. */
function Where({ owned }: { owned: OwnedCopy[] }) {
  if (owned.length === 0) return <span className="small muted">Not in your collection</span>;
  const short = (name: string) => name.replace('Catalog ', 'C').replace(' / Drawer ', ' D');
  return (
    <span className="small where-list">
      {owned.map((o, i) => (
        <span key={`${o.locationId}-${o.setCode}-${o.collectorNumber}-${o.finish}`}>
          {i > 0 && ' · '}
          <Link href={`/locations/${o.locationId}`} className="where">
            {o.kind === 'deck' ? `in ${o.locationName}` : short(o.locationName)}
            {o.divider && ` · ${o.divider}`}
          </Link>
          <span className="muted">
            {' '}
            ({o.quantity > 1 ? `${o.quantity}× ` : ''}
            {o.setCode.toUpperCase()} {o.collectorNumber}
            {o.foil && `, ${o.foil}`})
          </span>
        </span>
      ))}
    </span>
  );
}
